import { useTranslations } from '@vocab/react';
import {
  Box,
  ButtonLink,
  Columns,
  Column,
  IconPersonAdd,
  Stack,
  IconSearch,
  TextField,
} from 'braid-design-system';
import { useEffect, useState } from 'react';

import translations from 'src/App/App.vocab';
import { SkeletonUserList } from 'src/App/Components/Skeleton/SkeletonUserList/SkeletonUserList';
import TrackingLink from 'src/App/Components/TrackingLink/TrackingLink';
import { PageLayout } from 'src/App/Layouts/PageLayout/PageLayout';
import { PAGE_USER_LIST_DISPLAYED, USER_LIST } from 'src/constants/tracking';
import { URL_ADD_USER } from 'src/constants/urls';
import { useAccountContext } from 'src/hooks/use-account-context';
import { useDebounce } from 'src/hooks/use-debounce';
import { useTracking } from 'src/hooks/use-track-page-load';
import { useUserList } from 'src/hooks/use-user-list';
import { useConfig } from 'src/modules/ConfigProvider';

import { SearchUsersProvider } from './SearchUsers/SearchUsers';
import { UsersCount } from './UsersCount/UsersCount';
import { UsersList } from './UsersList/UsersList';

export const UsersListPage = () => {
  const { urlResolver } = useConfig();
  const { t } = useTranslations(translations);
  const { data: users, loading, error } = useUserList();
  const { trackView } = useTracking();
  const { data: accountContext } = useAccountContext();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm] = useDebounce(searchTerm, 500);

  useEffect(() => {
    if (!accountContext) {
      return;
    }
    trackView(PAGE_USER_LIST_DISPLAYED, USER_LIST);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountContext]);

  return (
    <PageLayout heading={t('Team list title')} width="medium">
      <Stack space="large">
        <Columns space="medium" alignY="top" collapseBelow="tablet" reverse>
          <Column width="content">
            <TrackingLink
              renderAs={ButtonLink}
              icon={<IconPersonAdd />}
              tone="brandAccent"
              href={urlResolver({ path: `/${URL_ADD_USER}` })}
            >
              {t('Add team member button')}
            </TrackingLink>
          </Column>
          <Column>
            <Box />
          </Column>
          <Column width="2/5">
            <TextField
              id="search"
              icon={<IconSearch />}
              onClear={() => setSearchTerm('')}
              placeholder={t('Search name or email placeholder')}
              aria-labelledby="Search team members"
              value={searchTerm}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setSearchTerm(e.target.value)
              }
            />
          </Column>
        </Columns>
        {!users || loading || error ? (
          <SkeletonUserList />
        ) : (
          <SearchUsersProvider searchTerm={debouncedSearchTerm}>
            <Stack space="medium">
              <UsersCount
                searchTerm={searchTerm}
                isSearchState={Boolean(searchTerm)}
              />
              <UsersList />
            </Stack>
          </SearchUsersProvider>
        )}
      </Stack>
    </PageLayout>
  );
};
